import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCollectionTooltip from '../hooks/useCollectionTooltip';

/**
 * Renders the hover-tooltip DOM node for collection detection features.
 *
 * The tooltip element is created imperatively (outside React's tree) because
 * OpenLayers Overlay relocates its element into OL's own overlay container.
 * If React owned the element directly, unmounting would trigger a
 * "removeChild: not a child of this node" error since OL already moved it.
 *
 * Instead, we:
 * 1. Create the DOM element once via useRef + document.createElement
 * 2. Pass that element to the hook (for OL overlay attachment)
 * 3. Portal React content into it (so React never tries to remove the element itself)
 *
 * @param {{ enabled: boolean }} props - Gate to disable interactions (e.g. no detections)
 */
const CollectionTooltip = ({ enabled }) => {
	const overlayElRef = useRef(null);
	if (!overlayElRef.current) {
		overlayElRef.current = document.createElement('div');
		overlayElRef.current.className = 'ol-tooltip';
	}

	const { tooltipContent } = useCollectionTooltip(enabled, overlayElRef);

	// Cleanup: if the element is still attached somewhere when this component
	// unmounts (shouldn't happen since the hook removes the overlay, but
	// defensive), detach it so it can be GC'd.
	useEffect(() => {
		return () => {
			overlayElRef.current?.remove();
		};
	}, []);

	return createPortal(tooltipContent, overlayElRef.current);
};

export default CollectionTooltip;
